import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import multiMonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";
import { Popover, Select } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchShiftCalendar,
  setShiftCalendarLoading,
  toggleShowShiftScheduleModal,
  updateShiftScheduleFilter,
} from "../../reducers/clientdashboard";
import { ShiftCalendarPopups } from "../common/client/dashboard/shifts/ShiftCalendarPopups";
import { ShiftScheduleTypesList } from "../common/client/dashboard/shifts/ShiftScheduleTypesList";
import ModalPopUp from "../common/ModalPopUp";
import "@/assets/css/fullCalendar.scss";
import { CalendarArrow } from "../Icons/CalendarArrow";
import { RejectIcon } from "../Icons/RejectIcon";
import { SelectDownIcon } from "../Icons/SelectDownIcon";
import DashboardShiftScheduleLoader from "../Skeletons/client/DashboardShiftScheduleLoader";
import useIsMobile from "../../utilities/hooks/useIsMobile";

const DashboardShiftScheduleModal = () => {
  const { expandShiftCalendar, shiftScheduleFilter, shiftCalendarLoading } =
    useSelector((state) => state.clientDashboard);

  const [activeCalender, setActiveCalender] = useState([
    dayjs(),
    dayjs().add(1, "month"),
  ]);
  const { showShiftScheduleModal } = useSelector(
    (state) => state.clientDashboard,
  );
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );
  const [selectYear, setSelectYear] = useState("month");
  const [currentYear, setCurrentYear] = useState(moment().year());
  const [currentMonth, setCurrentMonth] = useState(moment().month());
  const [initialDate, setInitialDate] = useState(moment().toDate());
  const [targetDate, setTargetDate] = useState(null); // New state to track the target date
  const dispatch = useDispatch();
  const calendarRef = useRef(null);

  const [activeMonth, setActiveMonth] = useState(0);
  const isMobile = useIsMobile();

  const options = [
    { value: "month", label: "Month" },
    { value: "year", label: "Year" },
  ];

  useEffect(() => {
    return () => {
      dispatch(toggleShowShiftScheduleModal(false));
      setCurrentMonth(moment().month());
      setCurrentYear(moment().year());
    };
  }, []);

  useEffect(() => {
    if (showShiftScheduleModal) {
      dispatch(setShiftCalendarLoading(true));
      dispatch(
        updateShiftScheduleFilter({
          shift_type: selectYear === "month" ? "month" : "year",
        }),
      );
      dispatch(
        fetchShiftCalendar({
          date: activeCalender[activeMonth].format("YYYY-MM-DD"),
          shift_type: selectYear === "month" ? "month" : "year",
        }),
      );
    }
  }, [showShiftScheduleModal, selectYear]);

  useEffect(() => {
    if (showShiftScheduleModal) {
      if (!isPremiumAccount) {
        dispatch(
          updateShiftScheduleFilter({
            shift_status: "booked",
          }),
        );
      }
    }
  }, [showShiftScheduleModal, isPremiumAccount]);

  const handleClose = () => {
    dispatch(updateShiftScheduleFilter({ shift_type: "month" }));
    dispatch(toggleShowShiftScheduleModal(false));
    setCurrentMonth(moment().month());
    setCurrentYear(moment().year());
    setTimeout(() => {
      setSelectYear("month");
    }, 500);
    dispatch(setShiftCalendarLoading(true));
    dispatch(
      fetchShiftCalendar({
        date: activeCalender[activeMonth].format("YYYY-MM-DD"),
        shift_type: "month",
      }),
    );
  };

  useEffect(() => {
    if (showShiftScheduleModal.length > 0) {
      const earliestDate = showShiftScheduleModal
        .map((dateObj) => moment(dateObj.contract_date, "DD/MM/YYYY"))
        .sort((a, b) => a - b)[0]; // Get the earliest date
      if (earliestDate) {
        setInitialDate(earliestDate.toDate());
        setCurrentYear(earliestDate.year());
      }
    }
  }, [showShiftScheduleModal]);

  // Function to handle the "prev" button click
  const handlePrevClick = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.prev();
      const currentDate = calendarApi.getDate();
      const newYear = moment(currentDate).year(); // Get the new year after the "prev" click
      const newMonth = moment(currentDate).month(); // Get the new month

      setCurrentYear(newYear);

      if (selectYear === "month") {
        setCurrentMonth(newMonth);
      }

      // if (newYear !== currentYear) {
      // Trigger API call to fetch data for the new month/year
      dispatch(setShiftCalendarLoading(true));
      dispatch(
        fetchShiftCalendar({
          date: moment(currentDate).format("YYYY-MM-DD"),
          shift_type: selectYear === "month" ? "month" : "year",
        }),
      );
      setTargetDate(currentDate); // Set the target date to ensure synchronization
      // }
    }
  };

  // Function to handle the "next" button click
  const handleNextClick = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.next();
      const currentDate = calendarApi.getDate();
      const newYear = moment(currentDate).year(); // Get the new year after the "next" click
      const newMonth = moment(currentDate).month(); // Get the new month

      setCurrentYear(newYear);
      if (selectYear === "month") {
        setCurrentMonth(newMonth);
      }

      // Check if the year has changed, and trigger API call if it has
      // if (newYear !== currentYear) {
      dispatch(setShiftCalendarLoading(true));
      dispatch(
        fetchShiftCalendar({
          date: moment().year(newYear).month(newMonth).format("YYYY-MM-DD"),
          shift_type: selectYear === "month" ? "month" : "year",
        }),
      );
      setTargetDate(currentDate); // Set the target date to ensure synchronization

      // }
    }
  };

  useEffect(() => {
    if (showShiftScheduleModal.length > 0) {
      const earliestDate = showShiftScheduleModal
        .map((dateObj) => moment(dateObj.contract_date, "DD/MM/YYYY"))
        .sort((a, b) => a - b)[0]; // Get the earliest date
      if (earliestDate) {
        setInitialDate(earliestDate.toDate());
        setCurrentYear(earliestDate.year());
      }
    }
  }, [showShiftScheduleModal]);

  useEffect(() => {
    // Synchronize calendar view with target date after data is loaded
    if (!shiftCalendarLoading && targetDate) {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(targetDate);
      }
      setTargetDate(null); // Reset target date after synchronization
    }
  }, [shiftCalendarLoading, targetDate]);

  // Month and Year display
  const getMonthName = (monthIndex) =>
    moment().month(monthIndex).format("MMMM");

  const handleOnCloseModal = () => {
    handleClose();
  };

  // month view fullCalendar

  // month view = find the data
  const formattedEvents = useMemo(() => {
    if (!expandShiftCalendar || Object.keys(expandShiftCalendar).length === 0)
      return null;

    return Object?.entries(expandShiftCalendar)?.flatMap(([date, events]) =>
      events.map((event) => ({
        title: `${moment(event.contract_start_time, "hh:mm:ss").format("HH:mm")} - ${moment(event.contract_end_time, "hh:mm:ss").format("HH:mm")}`,
        start: date,
        allDay: true,
        ...event,
      })),
    );
  }, [expandShiftCalendar]);

  // month - event class names
  const getEventClassNames = (status) => {
    switch (status) {
      case "Published":
        return "bg-red text-white py-2 px-3 rounded-lg mt-6 border-0 min-h-[40px]";
      case "Applied":
        return "bg-yellow text-black py-2 px-3 rounded-lg mt-6 border-0";
      case "Booked":
      case "Worked":
        return "bg-green text-white py-2 px-3 rounded-lg mt-6 border-0 min-h-[40px]";
      default:
        return "bg-gray-200 text-black border-0";
    }
  };

  // month - event content
  const renderEventContent = (eventInfo) => {
    const { main_contract_ref_no, contract_status, contract_date } =
      eventInfo.event.extendedProps;
    const data = [eventInfo.event.extendedProps];
    const popupContainer =
      document.querySelector(".fc-more-popover .fc-popover-body") ||
      document.querySelector(".manage_dashboard_shift_wrapper .month_view");

    return (
      <Popover
        getPopupContainer={() => popupContainer}
        overlayClassName="tooltip-z-index"
        position="top"
        content={<ShiftCalendarPopups data={data} shiftDate={contract_date} />}
      >
        <div className="flex cursor-pointer items-center justify-start gap-2 md:flex-wrap lg:flex-wrap xl:flex-wrap 2xl:flex-nowrap">
          <p
            className={`w-fit rounded-md px-[4px] py-[6px] text-[12px] font-medium ${
              contract_status === "Published"
                ? "bg-[#ffd9d9] text-red"
                : contract_status === "Applied"
                  ? "bg-[#ffffdd] text-yellow"
                  : contract_status === "Booked"
                    ? "bg-[#e5ffe5] text-green"
                    : "text-white"
            }`}
          >
            {main_contract_ref_no}
          </p>
          <p className="text-[14px] font-bold">{eventInfo.event.title}</p>
        </div>
      </Popover>
    );
  };

  // year view - popup
  const renderDayCell = (args) => {
    const date = moment(args.date).format("YYYY-MM-DD");
    const isCurrentMonth = activeMonth === moment(args.date).month();
    const hasShifts = expandShiftCalendar[date];

    if (hasShifts) {
      const shiftStatusObj = hasShifts.reduce((acc, shift) => {
        const status = shift.contract_status;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      return (
        <Popover
          rootClassName="z-[1033] ClientPayPopover_Wrapper"
          trigger={"hover"}
          placement="top"
          content={
            <ShiftCalendarPopups
              data={hasShifts}
              shiftDate={args.date.getDate()}
            />
          }
        >
          <div className="grid cursor-pointer place-items-center">
            <div className="bg-gradient-to-b text-[18px] font-medium">
              <div className="absolute left-[1px] top-[1px] flex h-[30px] w-[30px] flex-col overflow-hidden rounded-lg">
                {shiftStatusObj.Published > 0 && (
                  <span className="block flex-1 bg-red"></span>
                )}
                {shiftStatusObj.Applied > 0 && (
                  <span className="block flex-1 bg-yellow"></span>
                )}
                {(shiftStatusObj.Booked > 0 || shiftStatusObj.Worked > 0) && (
                  <span className="block flex-1 bg-green"></span>
                )}
              </div>
              <span
                className={`relative h-[20px] w-[20px] !font-medium ${Object.keys(shiftStatusObj).length === 0 ? "text-txtColor" : "text-white"}`}
              >
                {args.date.getDate()}
              </span>
            </div>
          </div>
        </Popover>
      );
    }

    // Render days without shifts
    return (
      <div className="flex flex-col place-items-center items-center justify-center">
        <div className="font-normaltext-txtColor h-[20px] w-[20px] text-[16px]">
          {args.date.getDate()}
        </div>
      </div>
    );
  };

  return (
    <ModalPopUp
      show={showShiftScheduleModal}
      onClose={() => {
        handleClose();
      }}
      panelClass={` ${selectYear == "month" ? "max-w-[98%] max-sm:max-w-full" : "max-w-[98%] max-sm:max-w-full"}`}
    >
      <div className="shiftDatesModal_wrapper manage_dashboard_shift_wrapper grid gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3 max-sm:flex-col max-sm:items-stretch">
          {/* Row 1: Title + nav + close */}
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <h3 className="whitespace-nowrap text-[20px] font-semibold leading-[normal] text-black max-sm:text-[16px]">
                Shift Schedule
              </h3>
              <div className="flex items-center rounded-lg bg-lightblueBG p-1">
                <button
                  className={`p-3 !text-blue max-sm:p-2 ${shiftCalendarLoading ? "pointer-events-none cursor-wait" : "cursor-pointer"} `}
                  onClick={handlePrevClick}
                >
                  <CalendarArrow cls="w-[12px] h-[11px] rotate-0 " />
                </button>
                <p className="flex gap-2 whitespace-nowrap px-2 text-lg font-semibold text-blue max-sm:px-1 max-sm:text-sm">
                  <span>
                    {selectYear == "month" && getMonthName(currentMonth)}
                  </span>
                  <span>{currentYear}</span>
                </p>
                <button
                  className={`${shiftCalendarLoading ? "pointer-events-none cursor-wait" : "cursor-pointer"} p-3 !text-blue max-sm:p-2`}
                  onClick={handleNextClick}
                >
                  <CalendarArrow cls="w-[13px] h-[12px] !text-blue -rotate-180 " />
                </button>
              </div>
            </div>
            <button
              onClick={handleOnCloseModal}
              className="shrink-0 text-red outline-none ring-0"
            >
              <RejectIcon />
            </button>
          </div>
          {/* Row 2: Filters + controls */}
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <ShiftScheduleTypesList year={"year"} />

            <div className="flex flex-wrap items-center justify-end gap-3 max-sm:gap-2">
              <div>
                <Select
                  className={`ease mt-1 min-h-[40px] w-[140px] overflow-hidden rounded-lg border border-lightGrey py-[0.13rem] !text-[19px] font-medium capitalize leading-[normal] text-black transition-all duration-200 focus:border-primary max-sm:min-h-[36px] max-sm:w-[110px] max-sm:!text-[14px]`}
                  value={selectYear}
                  suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                  onChange={(e) => {
                    setSelectYear(e);
                    dispatch(setShiftCalendarLoading(true));
                    setCurrentYear(moment().year());
                    setCurrentMonth(moment().month());
                  }}
                  placeholder="Select an option"
                >
                  {options.map(({ value, label }, index) => (
                    <Select.Option
                      key={index}
                      value={value}
                      className="text-[16px] font-semibold capitalize"
                    >
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>

        {selectYear == "month" ? (
          <div className="month_view rounded-[10px] bg-white pb-2 shadow-[0px_x1px_6px_0px_#E7EFFB]">
            <div className="flex w-full">
              <div className="flex w-full flex-col gap-6 sm:max-lg:flex-row">
                {
                  shiftCalendarLoading ? (
                    <div className="h-auto w-full overflow-x-hidden">
                      <DashboardShiftScheduleLoader />
                    </div>
                  ) : (
                    // formattedEvents?.length > 0 && formattedEvents ? (
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[
                        multiMonthPlugin,
                        interactionPlugin,
                        dayGridPlugin,
                      ]}
                      initialView={"dayGridMonth"}
                      initialDate={initialDate || moment().toDate()} // Fallback to today if initialDate is invalid
                      // selectLongPressDelay={100}
                      events={formattedEvents}
                      dayHeaderContent={(args) =>
                        moment(args.date).format(isMobile ? "ddd" : "dddd")
                      }
                      eventClassNames={(eventInfo) =>
                        getEventClassNames(
                          eventInfo.event.extendedProps.contract_status,
                        )
                      }
                      eventContent={renderEventContent}
                      // dayMaxEventRows={3}
                      // eventRender={
                      //     true
                      // }
                      // dayMaxEvents={true}
                      showNonCurrentDates={false}
                      fixedWeekCount={false}
                    />
                  )
                  // ) : null
                }
              </div>
            </div>
          </div>
        ) : (
          <div className="fullcalendar_wrapper select-none">
            {shiftCalendarLoading ? (
              <div className="h-auto !w-full !p-0">
                <DashboardShiftScheduleLoader
                  heading={{ width: 0, height: 0 }}
                  row={2}
                  column={6}
                />
              </div>
            ) : (
              <>
                <div>
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[
                      multiMonthPlugin,
                      interactionPlugin,
                      dayGridPlugin,
                    ]}
                    initialView="multiMonthYear"
                    // events={events}
                    dayHeaderContent={(args) => {
                      return moment(args.date).format(isMobile ? "d" : "dd");
                    }}
                    dayCellContent={(args) => renderDayCell(args)}
                    headerToolbar={{
                      left: "prev,next",
                      center: "title",
                      right: "multiMonthYear,dayGridMonth",
                    }}
                    fixedWeekCount={false}
                    showNonCurrentDates={false}
                    dayCellClassNames={() => {}}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </ModalPopUp>
  );
};

export default DashboardShiftScheduleModal;
